import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutUs = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/team`);
        setTeam(r.data.team);
      } catch {
        setTeam([]);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={isZh ? '关于阿里建筑 ALIGCUS | 30+ 年加州 ADU 经验 1500+ 项目 | 团队介绍' : 'About ALIGCUS Construction | 30+ Years California ADU Experience, 1500+ Projects | Our Team'}
        description={isZh ? 'ALIGCUS 阿里建筑专注加州 ADU 设计、施工与开发 30+ 年，落地 1500+ 项目。CEO Peter Li 领衔的专家团队为您打造高品质后院屋。' : 'ALIGCUS Construction has been designing, permitting and building California ADUs for 30+ years with 1500+ projects delivered. Meet our expert team led by CEO Peter Li.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/about' : 'https://www.aligcus.com/about'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/about' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/about' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '关于我们' : 'About Us', url: isZh ? 'https://www.aligcus.com/zh/about' : 'https://www.aligcus.com/about' },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://static.wixstatic.com/media/2de339_6a55f715f3e84d4da298ed01f5fe3f64~mv2.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{isZh ? '关于我们' : 'About Us'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {isZh ? 'ALIGCUS 是一家专注于建筑设计、施工和 ADU 开发的综合房地产公司。' : 'ALIGCUS is a comprehensive real estate company specializing in architecture, construction, and ADU development.'}
          </p>
        </div>
      </section>

      {/* Leadership Team — Two-Column Layout */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-16">{isZh ? '我们的团队' : 'Our Team'}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {team.map((member, idx) => (
              <div key={member.id || idx} className="flex items-center bg-gray-50 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow">
                {/* Circular Image with dark backdrop */}
                <div className="flex-shrink-0 mr-5">
                  <div className="overflow-hidden shadow-lg" style={{ width: '120px', height: '150px', borderRadius: '45%' }}>
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-0.5">{member.name}</h3>
                  <p className="text-red-600 font-semibold text-sm mb-2">{isZh ? member.role_zh : member.role_en}</p>
                  <div className="w-8 h-0.5 bg-red-600 rounded mb-2" />
                  <p className="text-gray-600 leading-relaxed text-sm">{isZh ? member.bio_zh : member.bio_en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-red-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{isZh ? '准备开始您的 ADU 项目？' : 'Ready to Start Your ADU Project?'}</h2>
          <p className="text-red-100 mb-8 text-lg">{isZh ? '联系我们获取免费咨询和估价' : 'Contact us for a free consultation and estimate'}</p>
          <div className="flex gap-4 justify-center">
            <Link to="/estimate"><Button size="lg" className="bg-white text-red-600 hover:bg-red-50 font-semibold px-8 py-4">{isZh ? '免费估价' : 'Free Estimate'}</Button></Link>
          </div>
          <div className="flex gap-3 justify-center mt-4">
            {[{l:'SoCal',n:'(626) 803-8000',t:'6268038000'},{l:'NorCal',n:'(408) 900-6688',t:'4089006688'},{l:'San Diego',n:'858-910-0000',t:'8589100000'}].map(p=>(
              <a key={p.l} href={`tel:${p.t}`} className="flex flex-col items-center gap-1 px-4 py-2 border border-white/30 rounded-xl hover:bg-white/10 transition-all">
                <span className="text-xs font-bold text-white/70">{p.l}</span>
                <span className="text-sm font-bold text-white">{p.n}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
